import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsIcon from '@mui/icons-material/Settings';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import GooglePlacesSearchModal from '../components/GooglePlacesSearchModal';

import MapPoiFormModal from '../components/MapPoiFormModal';
import MapConfigFormModal from '../components/MapConfigFormModal';
import ConfirmDialog from '../components/ConfirmDialog';
import { useListMapPois, useDeleteMapPoi } from '../services/appService';
import { useAuth } from '../context/AuthContext';

// Helper to format date/time
const formatDate = (dateInput) => {
    if (!dateInput) return 'N/A';
    try {
        const date = new Date(dateInput);
        return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleString();
    } catch (e) {
        return 'Invalid Date';
    }
};

function MapPoisPage() {
    const { appId } = useParams();
    const { isRedaktion } = useAuth();
    const [pois, setPois] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isPoiModalOpen, setIsPoiModalOpen] = useState(false);
    const [editingPoi, setEditingPoi] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [poiToDelete, setPoiToDelete] = useState(null);
    const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
    const [isBraveSearchModalOpen, setIsBraveSearchModalOpen] = useState(false);

    const listPoisHook = useListMapPois();
    const deletePoiHook = useDeleteMapPoi();

    const fetchPois = useCallback(async () => {
        if (!appId) return;
        setLoading(true);
        setError('');
        try {
            const data = await listPoisHook(appId);
            setPois(data || []);
        } catch (err) {
            console.error("Error fetching POIs:", err);
            setError(err.message || 'Failed to load POIs.');
        } finally {
            setLoading(false);
        }
    }, [appId, listPoisHook]);

    useEffect(() => {
        fetchPois();
    }, [fetchPois]);

    const handleOpenCreatePoiModal = () => {
        setEditingPoi(null);
        setIsPoiModalOpen(true);
    };

    const handleOpenEditPoiModal = (poi) => {
        setEditingPoi(poi);
        setIsPoiModalOpen(true);
    };

    const handleClosePoiModal = () => {
        setIsPoiModalOpen(false);
        setEditingPoi(null);
    };

    const handleSavePoi = () => {
        handleClosePoiModal();
        fetchPois();
    };

    const handleOpenConfigModal = () => {
        setIsConfigModalOpen(true);
    };

    const handleCloseConfigModal = () => {
        setIsConfigModalOpen(false);
    };

    const handleOpenBraveSearchModal = () => {
        setIsBraveSearchModalOpen(true);
    };

    const handleCloseBraveSearchModal = () => {
        setIsBraveSearchModalOpen(false);
    };

    const handlePoiAddedFromBrave = () => {
        fetchPois();
    };

    const handleOpenConfirmDialog = (poiId) => {
        setPoiToDelete(poiId);
        setConfirmDialogOpen(true);
    };

    const handleCloseConfirmDialog = () => {
        setPoiToDelete(null);
        setConfirmDialogOpen(false);
    };

    const handleDeleteConfirm = async () => {
        if (!poiToDelete || !appId) return;
        setLoading(true);
        setError('');
        try {
            await deletePoiHook(appId, poiToDelete);
            fetchPois();
        } catch (err) {
            console.error("Error deleting POI:", err);
            setError(err.message || 'Failed to delete POI.');
            setLoading(false);
        } finally {
            handleCloseConfirmDialog();
        }
    };

    const columns = [
        { field: 'id', headerName: 'ID', width: 150 },
        { field: 'title', headerName: 'Title', width: 250 },
        { field: 'category', headerName: 'Category', width: 150 },
        {
            field: 'latitude',
            headerName: 'Latitude',
            width: 120,
            type: 'number',
        },
        {
            field: 'longitude',
            headerName: 'Longitude',
            width: 120,
            type: 'number',
        },
        { field: 'description', headerName: 'Description', width: 300, sortable: false },
        {
            field: 'created_at',
            headerName: 'Created At',
            width: 180,
            type: 'dateTime',
            valueFormatter: (params) => formatDate(params?.value),
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 100,
            cellClassName: 'actions',
            getActions: ({ id, row }) => [
                <GridActionsCellItem
                    icon={<EditIcon />}
                    label="Edit POI"
                    onClick={() => handleOpenEditPoiModal(row)}
                    color="inherit"
                />,
                <GridActionsCellItem
                    icon={<DeleteIcon />}
                    label="Delete POI"
                    onClick={() => handleOpenConfirmDialog(id)}
                    color="inherit"
                />,
            ],
        },
    ];

    return (
        <Box sx={{ height: 'calc(100vh - 120px)', width: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexShrink: 0 }}>
                <Typography variant="h4" gutterBottom>
                    Manage Map Points of Interest
                </Typography>
                <Box>
                    <Button
                        variant="outlined"
                        startIcon={<SettingsIcon />}
                        onClick={handleOpenConfigModal}
                        sx={{ mr: 1 }}
                    >
                        Map Settings
                    </Button>
                    {!isRedaktion() && (
                      <Button
                          variant="outlined"
                          startIcon={<AutoFixHighIcon />}
                          onClick={handleOpenBraveSearchModal}
                          sx={{ mr: 1 }}
                      >
                          AI Filling
                      </Button>
                    )}
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpenCreatePoiModal}
                    >
                        Create POI
                    </Button>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>{error}</Alert>}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Box sx={{ flexGrow: 1, width: '100%' }}>
                    <DataGrid
                        rows={pois}
                        columns={columns}
                        pageSizeOptions={[10, 25, 50]}
                        initialState={{
                            pagination: {
                                paginationModel: { page: 0, pageSize: 10 },
                            },
                            sorting: {
                                sortModel: [{ field: 'created_at', sort: 'desc' }],
                            },
                        }}
                        checkboxSelection={false}
                        disableRowSelectionOnClick
                        sx={{
                            border: 0,
                            '& .MuiDataGrid-main': {
                                height: '100%',
                            },
                            '& .MuiDataGrid-virtualScroller': {
                                height: 'calc(100% - 56px)',
                                overflowY: 'auto'
                            },
                        }}
                    />
                </Box>
            )}

            <MapPoiFormModal
                open={isPoiModalOpen}
                onClose={handleClosePoiModal}
                onSave={handleSavePoi}
                poi={editingPoi}
                appId={appId}
            />

            <MapConfigFormModal
                open={isConfigModalOpen}
                onClose={handleCloseConfigModal}
                appId={appId}
            />

            <GooglePlacesSearchModal
                open={isBraveSearchModalOpen}
                onClose={handleCloseBraveSearchModal}
                onPoiAdded={handlePoiAddedFromBrave}
                appId={appId}
            />

            <ConfirmDialog
                open={confirmDialogOpen}
                onClose={handleCloseConfirmDialog}
                onConfirm={handleDeleteConfirm}
                title="Delete POI?"
                message={`Are you sure you want to delete POI ID: ${poiToDelete}? This action cannot be undone.`}
            />
        </Box>
    );
}

export default MapPoisPage; 